import { useEffect, useRef, useState, useCallback } from 'react';
import type { SettingsStore } from '@ports/SettingsStore.ts';
import type { VideoController } from '@ports/VideoController.ts';
import type { SubtitleStore } from '../state/subtitleStore.ts';
import {
  SubtitleSyncEngine,
  type AnimationScheduler,
  type SyncSnapshot,
} from '../sync/subtitleSyncEngine.ts';
import { DEFAULT_SETTINGS } from '@core/settings/schema.ts';

export type UseSubtitleSyncOptions = {
  scheduler?: AnimationScheduler;
  documentRef?: Document;
  /** When this changes, reset store + clear overlay (W-SYNC-08). */
  videoId?: string | null;
  /** Host element for #mainSub / #secondSub (extension OverlayRenderer root). */
  getOverlayRoot?: () => HTMLElement | null;
};

const EMPTY: SyncSnapshot = {
  main: null,
  second: null,
  settings: DEFAULT_SETTINGS,
  revision: 0,
};

export type UseSubtitleSyncResult = SyncSnapshot & {
  invalidate: () => void;
};

export function useSubtitleSync(
  video: VideoController | null,
  store: SubtitleStore,
  settingsStore: SettingsStore,
  options: UseSubtitleSyncOptions = {},
): UseSubtitleSyncResult {
  const [snapshot, setSnapshot] = useState<SyncSnapshot>(EMPTY);
  const engineRef = useRef<SubtitleSyncEngine | null>(null);
  const prevVideoId = useRef<string | null | undefined>(undefined);
  const getOverlayRootRef = useRef(options.getOverlayRoot);
  getOverlayRootRef.current = options.getOverlayRoot;

  useEffect(() => {
    if (!video) {
      engineRef.current?.stop();
      engineRef.current = null;
      setSnapshot(EMPTY);
      return;
    }

    const engine = new SubtitleSyncEngine(store, video, settingsStore, {
      scheduler: options.scheduler,
      documentRef: options.documentRef,
      onUpdate: setSnapshot,
      getOverlayRoot: () => getOverlayRootRef.current?.() ?? null,
    });
    engineRef.current = engine;
    void engine.start();

    return () => {
      engine.stop();
      if (engineRef.current === engine) engineRef.current = null;
    };
  }, [video, store, settingsStore, options.scheduler, options.documentRef]);

  useEffect(() => {
    if (prevVideoId.current === undefined) {
      prevVideoId.current = options.videoId;
      return;
    }
    if (prevVideoId.current === options.videoId) return;
    prevVideoId.current = options.videoId;
    engineRef.current?.resetNavigation();
  }, [options.videoId]);

  const invalidate = useCallback(() => {
    engineRef.current?.invalidate();
  }, []);

  return {
    ...snapshot,
    invalidate,
  };
}
